import { ComponentProps, FC } from "react";
import * as _ from "lodash";
import moment from "moment";

import { ChartTimePeriod, ISODuration } from "@ctra/api";
import { defaultLocale, Enterprise, useTranslation } from "@ctra/i18n";
import { Nullable, TS, defaultBucketSizes, clampChartUnitList } from "@ctra/utils";
import { GAEvent } from "@ctra/analytics";

import {
  AntDesignDatePicker,
  AntDesignInput,
  BlockOutlined,
  CalendarOutlined,
  Charts,
  DownOutlined,
  Menu,
  Space
} from "@ctra/components";

import { useLocalization } from "@base";
import { GACategories } from "@analytics";

import styles from "./ISODurationFilter.module.less";

const { SimpleDropdown } = Charts;

interface ISODurationFilterProps {
  /**
   * available iso durations
   */
  durations: Readonly<Array<ISODuration>>;
  /**
   * available time units
   */
  buckets?: Array<ISODuration>;
  /**
   * selected duration
   */
  duration: Nullable<ISODuration>;
  /**
   * selected time unit
   */
  bucketSize: ISODuration;
  /**
   * time period for pre-filled date range
   */
  timePeriod: ChartTimePeriod;
  /**
   * handle duration change
   * @param {Nullable<ISODuration>} duration
   */
  onDurationChange: (duration: Nullable<ISODuration>) => void;
  /**
   * handle custom date range changes
   * @param {ChartTimePeriod} timePeriod
   */
  onTimePeriodChange: (timePeriod: ChartTimePeriod) => void;
  /**
   * handle time unit change
   * @param {Nullable<ISODuration>} bucketSize
   */
  onUnitChange?: (bucketSize: ISODuration) => void;
  /**
   * disabled state
   */
  disabled?: boolean;
}

const { RangePicker } = AntDesignDatePicker;
const { Group } = AntDesignInput;
const { Item } = Menu;

/**
 * custom string as a constant to be used as key
 */
const custom = "custom";

/**
 * ISO duration switch
 * @param {string | null | undefined} duration
 * @param {Array<ISODuration>} durations
 * @param {{startDate: string, endDate: string} | Record<string, never>} timePeriod
 * @param {Array<ISODuration> | undefined} buckets
 * @param {string | null | undefined} bucketSize
 * @param {(duration: Nullable<ISODuration>) => void} onDurationChange
 * @param {(timePeriod: ChartTimePeriod) => void} onTimePeriodChange
 * @param {((bucketSize: Nullable<ISODuration>) => void) | undefined} onUnitChange
 * @param {boolean | undefined} disabled
 * @returns {JSX.Element}
 * @constructor
 */
const ISODurationFilter: FC<ISODurationFilterProps> = ({
  duration,
  durations,
  timePeriod,
  buckets = defaultBucketSizes,
  bucketSize,
  onDurationChange,
  onTimePeriodChange,
  onUnitChange = _.noop,
  disabled
}) => {
  const { startDate, endDate } = timePeriod;
  const { t } = useTranslation();
  const { dateFormat } = useLocalization();

  const {
    gamePlan: {
      wizard: {
        form: { placeholder }
      }
    },
    chart: { timePeriod: timeLabel }
  } = Enterprise;

  const handleChange = (key: string | number) => {
    if (key === custom) {
      onDurationChange(null);
    } else {
      onDurationChange(key as ISODuration);
    }
  };

  /**
   * pre-filled date range when the range picker is made visible
   */
  const defaultRange: ComponentProps<typeof RangePicker>["defaultValue"] = [
    TS.asMoment(startDate),
    TS.asMoment(endDate)
  ];

  /**
   * 1) Convert moment array that comes from antd range picker to epochs
   * 2) Send epochs to time period setter
   */
  const handleTimeChange: ComponentProps<typeof RangePicker>["onChange"] = (values) => {
    if (values) {
      const [startDate, endDate] = values;

      const startDateISOString = startDate?.startOf("day").toISOString(true);
      const endDateEOD = endDate?.endOf("day");

      const endDateISOString = (endDateEOD?.isAfter(moment()) ? endDateEOD : endDateEOD)?.toISOString(true);

      onTimePeriodChange({
        startDate: startDateISOString,
        endDate: endDateISOString
      } as unknown as ChartTimePeriod);
    }
  };

  /**
   * extra "custom" item in the date drop down list
   */
  const customItem = <Item key={custom}>{t<string>(timeLabel(custom), { variant: null })}</Item>;

  return (
    <Group compact>
      <SimpleDropdown
        disabled={disabled}
        values={durations}
        currentValue={_.isNull(duration) ? custom : duration}
        onChange={({ key }) => {
          GAEvent(
            GACategories.configurator,
            "Change time period",
            `${t<string>(timeLabel(key), { lng: defaultLocale, variant: null })}`
          );

          handleChange(key);
        }}
        showLabel={true}
        getLabel={(key, { isToggle }) => (
          <Space align="center">
            {key === duration && !_.isNull(duration) && <CalendarOutlined />}
            {key && t<string>(timeLabel(key), { variant: null })}
            {isToggle && <DownOutlined className={styles.Caret} />}
          </Space>
        )}
        renderExtra={customItem}
      />
      {_.isNull(duration) && (
        <RangePicker
          disabled={disabled}
          format={dateFormat}
          dropdownClassName={styles.RangePicker}
          allowClear={false}
          placeholder={[t<string>(placeholder.startEpoch), t<string>(placeholder.endEpoch)]}
          value={defaultRange}
          onChange={(values, formatString) => {
            GAEvent(GACategories.configurator, "Change time period", "custom time");

            handleTimeChange(values, formatString);
          }}
        />
      )}
      {!!_.size(buckets) && (
        <SimpleDropdown
          disabled={disabled}
          values={clampChartUnitList(duration || timePeriod, buckets)}
          currentValue={bucketSize}
          onChange={({ key }) => {
            GAEvent(
              GACategories.configurator,
              "Change time unit",
              `${t<string>(timeLabel(key), { lng: defaultLocale, variant: "frequency" })}`
            );

            onUnitChange(key);
          }}
          showLabel={true}
          getLabel={(key, { isToggle }) => (
            <Space align="center">
              {key === bucketSize && !_.isNull(bucketSize) && <BlockOutlined />}
              {key && t<string>(timeLabel(key), { variant: "frequency" })}
              {isToggle && <DownOutlined className={styles.Caret} />}
            </Space>
          )}
        />
      )}
    </Group>
  );
};

export default ISODurationFilter;
